import { motion } from "framer-motion";
import { Coffee, UtensilsCrossed, Trophy, Lock, Crown } from "lucide-react";
import { PROGRAM, SPEAKERS_BY_SLUG } from "@/lib/programData";

function SpeakerRef({ slug }) {
  const s = SPEAKERS_BY_SLUG[slug];
  if (!s) return null;
  return (
    <a
      href={`#speaker-${slug}`}
      className="block group hover:text-[#F5892E] transition-colors"
      data-testid={`program-speaker-${slug}`}
    >
      <div className="font-display uppercase text-[#F5EBD9] text-base sm:text-lg leading-tight group-hover:text-[#F5892E] transition-colors">
        {s.name}
      </div>
      <div className="mt-1 text-xs sm:text-sm text-[#F5EBD9]/60 leading-snug">
        {s.role}
      </div>
    </a>
  );
}

function MetaRow({ item, i }) {
  const Icon =
    item.icon === "coffee" ? Coffee : item.icon === "food" ? UtensilsCrossed : null;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5, delay: (i % 8) * 0.03 }}
      className="grid grid-cols-12 gap-4 py-5 sm:py-6 border-b border-[#F5EBD9]/10 items-center"
    >
      <div className="col-span-12 sm:col-span-3 font-mono text-xs sm:text-sm text-[#F5EBD9]/60 tracking-wider">
        {item.time}
      </div>
      <div className="col-span-12 sm:col-span-9 flex items-center gap-3 text-[#F5EBD9]/70">
        {Icon && <Icon className="w-4 h-4 text-[#F5892E]" strokeWidth={2} />}
        <span className="font-serif italic text-lg sm:text-xl">{item.title}</span>
        {item.subtitle && (
          <span className="text-sm text-[#F5EBD9]/50">· {item.subtitle}</span>
        )}
      </div>
    </motion.div>
  );
}

function Slot({ item, i }) {
  if (item.type === "meta") return <MetaRow item={item} i={i} />;

  const isUnicredit = item.highlight === "unicredit";
  const isAward = item.highlight === "award";
  const isVip = item.highlight === "vip";
  const speakers = item.speakers || [];
  const moderators = item.moderators || (item.moderator ? [item.moderator] : []);
  const anchorId = speakers[0] ? `program-${speakers[0]}` : undefined;

  let containerCls = "grid grid-cols-12 gap-6 py-8 sm:py-10 border-b border-[#F5EBD9]/12";
  if (isUnicredit)
    containerCls += " bg-gradient-to-r from-[#F5892E]/10 via-[#F5892E]/5 to-transparent px-4 sm:px-6 -mx-4 sm:-mx-6";
  if (isAward)
    containerCls += " bg-gradient-to-r from-[#E0B95C]/12 via-[#E0B95C]/5 to-transparent px-4 sm:px-6 -mx-4 sm:-mx-6";
  if (isVip)
    containerCls += " bg-gradient-to-r from-[#E0B95C]/10 via-transparent to-[#F5892E]/8 px-4 sm:px-6 -mx-4 sm:-mx-6 border border-[#E0B95C]/30";

  return (
    <motion.div
      id={anchorId}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay: (i % 8) * 0.03 }}
      data-testid={`program-slot-${i}`}
      className={containerCls}
    >
      <div className="col-span-12 sm:col-span-3">
        <div className="font-mono text-sm sm:text-base text-[#F5892E] tracking-wider mb-2">
          {item.time}
        </div>
        {isUnicredit && (
          <div className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.28em] text-[#F5892E]">
            <Crown className="w-3 h-3" strokeWidth={2.5} />
            Главен спонсор
          </div>
        )}
        {isAward && (
          <div className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.28em] text-[#E0B95C]">
            <Trophy className="w-3 h-3" strokeWidth={2.5} />
            Церемония
          </div>
        )}
        {isVip && (
          <div className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.28em] text-[#E0B95C]">
            <Lock className="w-3 h-3" strokeWidth={2.5} />
            VIP Only
          </div>
        )}
      </div>

      <div className="col-span-12 sm:col-span-9">
        <h3 className={`font-display uppercase leading-[1.05] mb-4 ${
          isAward || isVip ? "text-2xl sm:text-3xl lg:text-4xl text-[#E0B95C]" : "text-xl sm:text-2xl lg:text-3xl text-[#F5EBD9]"
        }`}>
          {item.title}
        </h3>
        {item.subtitle && (
          <div className="text-sm sm:text-base text-[#F5EBD9]/70 mb-4 leading-relaxed">
            {item.subtitle}
          </div>
        )}
        {item.sponsor && (
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#F5892E] mb-4">
            {item.sponsor}
          </div>
        )}

        {speakers.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4">
            {speakers.map((slug) => (
              <SpeakerRef key={slug} slug={slug} />
            ))}
          </div>
        )}

        {item.tba && (
          <div className="mt-2 mb-4 font-mono text-xs uppercase tracking-[0.28em] text-[#F5EBD9]/50">
            {item.tba}
          </div>
        )}

        {moderators.length > 0 && (
          <div className="pt-4 border-t border-[#F5EBD9]/10">
            <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#F5EBD9]/50 mb-2">
              {moderators.length > 1 ? "Водещи" : "Водещ"}
            </div>
            <div className="flex flex-wrap gap-4 sm:gap-6">
              {moderators.map((slug) => (
                <SpeakerRef key={slug} slug={slug} />
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default function Program() {
  return (
    <section
      id="program"
      data-testid="program-section"
      className="relative bg-[#0F2647] text-[#F5EBD9] py-24 sm:py-32 lg:py-40 overflow-hidden border-t border-[#F5EBD9]/10"
    >
      <div className="max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9 }}
          className="mb-14 lg:mb-20"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#F5892E]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#F5EBD9]/70">
              Денят — Програма · 24 октомври 2026
            </span>
          </div>
          <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
            Един ден. <span className="text-[#F5892E]">Цялата картина.</span>
          </h2>
        </motion.div>

        <div className="border-t border-[#F5EBD9]/15">
          {PROGRAM.map((item, i) => (
            <Slot key={i} item={item} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
